import { Controller } from "@hotwired/stimulus";

type ChartItem = HTMLElement | SVGElement;

export default class extends Controller {
  static targets = ["row", "sector"];
  declare readonly rowTargets: HTMLElement[];
  declare readonly sectorTargets: SVGElement[];

  private hovered: ChartItem | null = null;
  private focused: ChartItem | null = null;
  private selected: ChartItem | null = null;

  connect() {
    this.hovered = null;
    this.focused = null;
    this.selected = null;
    this.render();
  }

  private get items(): ChartItem[] {
    return [...this.rowTargets, ...this.sectorTargets];
  }

  private item(target: EventTarget | null): ChartItem | null {
    if (!(target instanceof Element)) return null;
    const item = target.closest<ChartItem>("[data-donut-target]");
    return item && this.element.contains(item) ? item : null;
  }

  private categories(item: ChartItem): string[] {
    const ids = item.dataset.chartCategories?.split(" ") ?? [item.dataset.categoryId ?? ""];
    const group = Array.from(this.element.querySelectorAll<HTMLDetailsElement>("details[data-chart-rest]"))
      .find((details) => !details.open &&
        details.dataset.analysisSource === item.dataset.analysisSource &&
        details.dataset.chartCategories?.split(" ").some((id) => ids.includes(id)));
    return group?.dataset.chartCategories?.split(" ") ?? ids;
  }

  private sectors(item: ChartItem): SVGElement[] {
    const ids = this.categories(item);
    return this.sectorTargets.filter((sector) =>
      sector.dataset.analysisSource === item.dataset.analysisSource &&
      ids.includes(sector.dataset.categoryId ?? ""),
    );
  }

  private related(item: ChartItem | null): Set<ChartItem> {
    if (!item) return new Set();
    const sectors = this.sectors(item);
    return new Set<ChartItem>([
      item,
      ...sectors,
      ...this.rowTargets.filter((row) => sectors.some((sector) =>
        sector.dataset.analysisSource === row.dataset.analysisSource &&
        this.categories(row).includes(sector.dataset.categoryId ?? ""),
      )),
    ]);
  }

  private render() {
    const active = this.related(this.hovered ?? this.focused ?? this.selected);
    const selected = this.related(this.selected);
    for (const item of this.items) {
      item.toggleAttribute("data-chart-active", active.has(item));
      if (item.tagName.toLowerCase() !== "summary") {
        item.setAttribute("aria-pressed", String(selected.has(item)));
      }
    }
  }

  disclose() {
    // Disclosure changes the selection boundary; do not keep a hidden row active.
    this.hovered = null;
    this.focused = null;
    this.selected = null;
    this.render();
  }

  hover(event: PointerEvent) {
    // Touch selection is click-driven, not a sticky emulated hover.
    if (event.pointerType === "touch") return;
    this.hovered = this.item(event.target);
    this.render();
  }

  leave(event: PointerEvent) {
    if (event.pointerType === "touch") return;
    this.hovered = this.item(event.relatedTarget);
    this.render();
  }

  focus(event: FocusEvent) {
    this.focused = this.item(event.target);
    this.render();
  }

  blur(event: FocusEvent) {
    this.focused = this.item(event.relatedTarget);
    this.render();
  }

  select(event: MouseEvent) {
    const item = this.item(event.target);
    if (item) {
      this.selected = this.related(this.selected).has(item) ? null : item;
      this.hovered = null;
      this.focused = null;
    } else if (!(event.target instanceof Node) || !this.element.contains(event.target)) {
      this.selected = null;
      this.hovered = null;
      this.focused = null;
    } else {
      return;
    }
    this.render();
  }

  key(event: KeyboardEvent) {
    if (event.key === "Escape") {
      this.selected = null;
      this.hovered = null;
      this.focused = null;
      this.render();
    } else if ((event.key === "Enter" || event.key === " ") && this.item(event.target) instanceof SVGElement) {
      event.preventDefault();
      // Native legend buttons already produce their own keyboard click.
      this.item(event.target)?.dispatchEvent(new MouseEvent("click", { bubbles: true }));
    }
  }
}
